/* eslint-disable multiline-ternary */
import React, { cloneElement, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Divider,
  Grid,
  Hidden,
  Typography,
  Button
} from '@mui/material'
import { FilterListTwoTone, SearchTwoTone } from '@mui/icons-material'
import FilterBarTabs from './tabs'
import { useFormFactor } from '../../hooks'
import { useFilterState } from './hooks'
import { useStyles } from './style'
/** Compose several filters and a sorter in a responsive filter bar */
const FilterBar = props => {
  const classes = useStyles(props)
  const { isDesktop } = useFormFactor()

  const {
    disabled,
    filters,
    sorter,
    search,
    onClearAll,
    onApply,
    values
  } = props

  const filterProps = useMemo(
    () =>
      filters ? filters.map(({ props: { name } }) => name.toLowerCase()) : [],
    [filters]
  )

  const {
    handleChange,
    changed,
    filterValues,
    filterCount: numActive
  } = useFilterState(values, filterProps)

  const filterTab = {
    label: 'Filter',
    icon: <FilterListTwoTone className={classes.icon} />,
    component: filters && (
      <>
        <Hidden lgUp>
          <Grid item>
            <Typography variant='body1'>Filter by:</Typography>
          </Grid>
        </Hidden>
        {filters.map(filter => {
          const name = filter.props.name
          return (
            <Grid key={name} item>
              {cloneElement(filter, {
                onChange: handleChange,
                value: filterValues[name],
                disableApply: !changed.has(name),
                onApply
              })}
            </Grid>
          )
        })}
      </>
    )
  }

  const sortTab = {
    label: 'Sort',
    component: sorter && (
      <>
        <Grid item>
          <Typography variant='body1' noWrap>
            Sort by:
          </Typography>
        </Grid>
        <Grid item>
          {cloneElement(sorter, {
            label: filterValues.sort || sorter.props.options[0].key,
            value: filterValues.sort,
            onChange: handleChange,
            onApply
          })}
        </Grid>
      </>
    )
  }

  const searchTab = {
    label: 'Search',
    icon: <SearchTwoTone />,
    component: search && <Grid item>{search}</Grid>
  }

  const clearFiltersComponent = !!numActive && (
    <Button
      color='primary'
      onClick={onClearAll}
      fullWidth
      disabled={disabled}
      className={classes.clear}
    >
      <Typography variant='body2' className={classes.clearText}>
        {`(${numActive}) Clear Filters`}
      </Typography>
    </Button>
  )

  return isDesktop ? (
    <Grid
      container
      justifyContent='flex-start'
      alignItems='center'
      wrap='nowrap'
      spacing={2}
      className={classes.root}
    >
      {filters && (
        <>
          <Grid item>{filterTab.icon}</Grid>
          <Grid item>{clearFiltersComponent}</Grid>
          {filterTab.component}
          {sorter && (
            <Grid item>
              <Divider orientation='vertical' className={classes.divider} />
            </Grid>
          )}
        </>
      )}
      <Grid item>{sortTab.icon}</Grid>
      {sortTab.component}
      {search &&
        cloneElement(searchTab.component, { className: classes.searchItem })}
    </Grid>
  ) : (
    <Box className={classes.root}>
      <FilterBarTabs
        tabs={[
          ...(filters ? [filterTab] : []),
          ...(sorter ? [sortTab] : []),
          ...(search ? [searchTab] : [])
        ]}
      />
      {clearFiltersComponent}
    </Box>
  )
}

export default FilterBar

FilterBar.propTypes = {
  /** List of filter components to be displayed in the filter section */
  filters: PropTypes.arrayOf(PropTypes.element),
  /** Sort component to be displayed in the sort section */
  sorter: PropTypes.element,
  /** Search component for playlists */
  search: PropTypes.element,
  /** Current values of the filters and sorter.
   * Use the name of the filter as the key
   * e.g ``` { type: ['Option one'], sort: 'New' } ```
   */
  values: PropTypes.object,
  /**
   *  Callback function fired when the Apply button on any filter is clicked.
   */
  onApply: PropTypes.func,
  /**
   *  Callback function fired when the ```Clear all``` button is clicked
   */
  onClearAll: PropTypes.func
}
